import {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";

import apiService from "../services/api";
import { buildDetailNavigationState } from "../utils/navigation";

import { useAuth } from "./AuthContext";

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }

  return context;
};

export const SearchProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [previousUserId, setPreviousUserId] = useState(null);

  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowResults(false);

      return;
    }

    setIsSearching(true);
    setShowResults(true);

    try {
      // Perform global search via API
      const data = await apiService.globalSearch(query);

      // FAQs are now included in the API response
      setSearchResults({
        ...data,
        faq: data.faqs || [],
      });
    } catch (error) {
      console.error("Search error:", error);

      setSearchResults({
        registrations: [],
        transactions: [],
        faqs: [],
        tickets: [],
        ticketComments: [],
        faq: [],
        error: "Search service is currently unavailable.",
      });
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleResultClick = useCallback(
    (type, id, data = {}) => {
      setShowResults(false);
      setSearchQuery("");
      setSearchResults(null);

      switch (type) {
        case "customer":
          navigate(`/customers/${id}`);
          break;
        case "ticket":
          navigate(`/tickets/${id}`);
          break;
        case "invoice":
          navigate(`/invoices/${id}`, {
            state: buildDetailNavigationState(location),
          });
          break;
        case "user":
          navigate(`/users/${id}`, {
            state: buildDetailNavigationState(location),
          });
          break;
        case "admin":
          navigate("/users");
          break;
        case "faqs":
          navigate("/faqs");
          break;
        case "billing":
          navigate(`/invoices/${id}`, {
            state: buildDetailNavigationState(location),
          });
          break;
        case "lunakey":
          // Pass the redeem code ID as a query parameter
          navigate(`/customers/${id}?lunakey=${data.redeemCodeId}`);
          break;
        case "lunovakey":
          navigate(`/customers/${id}`);
          break;
        case "redeemcode":
          navigate(`/customers/${id}`);
          break;
        default:
          break;
      }
    },
    [location, navigate]
  );

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults(null);
    setShowResults(false);
  }, []);

  useEffect(() => {
    if (user && user.id !== previousUserId) {
      clearSearch();
      setPreviousUserId(user.id);
    }
  }, [user, previousUserId, clearSearch]);

  const value = {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    performSearch,
    handleResultClick,
    clearSearch,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};
